import { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '';

/**
 * ContractsTab — manage an organisation's CMS contracts and their PBPs.
 *
 * This screen did not exist. Contracts could only be entered during the
 * onboarding wizard, so once an organisation finished onboarding there was no
 * way to add one, correct one, or add a second PBP. An organisation that
 * finished onboarding without a contract, which is what happened to the first
 * live client, was permanently stuck: Mandate refuses every department-bound
 * user whose department has no contract and PBP, and there was no screen
 * anywhere to supply one.
 *
 * SOC 2 CC6.1 — writes go to the audited org-admin endpoints:
 *   POST /api/contracts            (org superadmin only, syncs to Mandate)
 *   POST /api/contracts/:id/pbps   (adds a PBP to an existing contract)
 */
export default function ContractsTab({ departments = [], onChanged, showToast, orgId = null }) {
  // Inside a client workspace (TPA administrator or platform support) every
  // call names that client; otherwise the session's own organization.
  const q = orgId ? `?org=${orgId}` : '';
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPbp, setNewPbp] = useState('');

  const [pbpFor, setPbpFor] = useState(null);
  const [extraPbp, setExtraPbp] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/contracts${q}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setContracts(data.contracts || []);
    } catch { /* leave the list empty; the warning below still shows */ }
    setLoading(false);
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const addContract = async () => {
    const name = newName.trim();
    const pbp = newPbp.trim();
    if (!name) { showToast('error', 'Contract number required', 'For example H0982.'); return; }
    if (!pbp) {
      showToast('error', 'PBP required',
        'Mandate refuses department users unless their department has a contract AND a PBP.');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API}/api/contracts${q}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, plan_id: pbp }),
      });
      const data = await res.json().catch(() => ({}));

      // Fail closed: only an explicit success counts as saved.
      if (!res.ok || data.success !== true) {
        showToast('error', 'Contract NOT saved', data.message || `Server returned ${res.status}`);
      } else {
        showToast('success', `Contract ${name} added`, `PBP ${pbp} created. Precision Mandate routes on it immediately.`);
        setNewName(''); setNewPbp(''); setAdding(false);
        await load(); onChanged?.();
      }
    } catch (err) {
      showToast('error', 'Network error', err.message + ' — the contract was NOT saved.');
    }
    setSaving(false);
  };

  // syncToMandate removed 2026-08-12 along with the "Sync to Mandate" button.
  // It pushed the contract into the standalone Mandate app's own contracts
  // table; that app is shut down and Precision Mandate routes on this table.

  const addPbp = async (contractId) => {
    const pbp = extraPbp.trim();
    if (!pbp) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/contracts/${contractId}/pbps${q}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pbp_id: pbp }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        showToast('error', 'PBP NOT added', data.message || `Server returned ${res.status}`);
      } else {
        showToast('success', `PBP ${pbp} added`, 'Assign it to departments under the Departments tab.');
        setExtraPbp(''); setPbpFor(null);
        await load(); onChanged?.();
      }
    } catch (err) {
      showToast('error', 'Network error', err.message);
    }
    setSaving(false);
  };

  const noContracts = !loading && contracts.length === 0;

  return (
    <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/50">
        <div>
          <p className="text-sm font-semibold text-white">Contracts &amp; PBPs</p>
          <p className="text-xs text-slate-500 mt-0.5">
            The CMS contract numbers this organization operates under. Departments are assigned to a
            contract and PBP, and Mandate refuses any department user whose department has neither.
          </p>
        </div>
        <button
          onClick={() => { setAdding(true); setNewName(''); setNewPbp(''); }}
          className="px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg font-medium text-sm text-white hover:shadow-lg transition flex-shrink-0"
        >
          + Add Contract
        </button>
      </div>

      {noContracts && (
        <div className="px-6 py-4 bg-amber-500/10 border-b border-amber-500/30 text-sm text-amber-200">
          This organization has no contract. Every department user will be rejected by Mandate until one
          is added, and {departments.length} department(s) are waiting on it.
        </div>
      )}

      {adding && (
        <div className="px-6 py-5 bg-blue-500/5 border-b border-blue-500/20 space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold block mb-1">
                Contract number *
              </label>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="H0982"
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold block mb-1">
                PBP / Plan ID *
              </label>
              <input
                value={newPbp}
                onChange={(e) => setNewPbp(e.target.value)}
                placeholder="002"
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
              />
              <p className="mt-1 text-[11px] text-slate-500">
                More PBPs can be added to this contract afterwards.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <button onClick={() => setAdding(false)} disabled={saving}
              className="px-3 py-1.5 text-sm text-slate-300 hover:text-white disabled:opacity-50">
              Cancel
            </button>
            <button onClick={addContract} disabled={saving || !newName.trim() || !newPbp.trim()}
              className="px-4 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg disabled:opacity-50">
              {saving ? 'Saving…' : 'Save Contract'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="px-6 py-10 text-center text-slate-500 text-sm">Loading…</div>
      ) : contracts.length === 0 ? (
        <div className="px-6 py-10 text-center text-slate-500 text-sm">No contracts yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-400 border-b border-slate-700/50">
              <th className="px-6 py-3 font-medium">Contract</th>
              <th className="px-4 py-3 font-medium">PBPs</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {contracts.map((c) => (
              <tr key={c.id} className="border-b border-slate-700/30">
                <td className="px-6 py-3 text-white font-medium">{c.name}</td>
                <td className="px-4 py-3">
                  {(c.pbps || []).length === 0 ? (
                    <span className="text-amber-300 text-xs">none — department users cannot be set up</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {(c.pbps || []).map((p) => (
                        <span key={p} className="px-2 py-0.5 rounded-md bg-slate-700/60 border border-slate-600 text-slate-200 text-xs">
                          {p}
                        </span>
                      ))}
                    </div>
                  )}
                  {pbpFor === c.id && (
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        value={extraPbp}
                        onChange={(e) => setExtraPbp(e.target.value)}
                        placeholder="010"
                        className="w-24 bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-white"
                      />
                      <button onClick={() => addPbp(c.id)} disabled={saving || !extraPbp.trim()}
                        className="px-2.5 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded-md disabled:opacity-50">
                        Add
                      </button>
                      <button onClick={() => { setPbpFor(null); setExtraPbp(''); }}
                        className="text-xs text-slate-400 hover:text-white">Cancel</button>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => { setPbpFor(c.id); setExtraPbp(''); }}
                      className="px-3 py-1 bg-slate-700/60 hover:bg-slate-700 border border-slate-600 text-slate-200 text-xs font-medium rounded-md transition"
                    >
                      + PBP
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
